/**
 * @file LieuxHub.tsx
 * @summary Page lieu / commune
 *
 * Page SEO locale ciblant une commune autour de Saint-Pierre-de-Chandieu (silo géographique).
 *
 * @see docs/07-CODEBASE.md — conventions de code
 * @see docs/02-FRONTEND.md — design system (tokens HSL, typographie)
 */
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import ServicePageLayout from "@/components/ServicePageLayout";
import { communes } from "@/data/communes";

const URL = "https://crossfitchistera.fr/lieux";

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Communes desservies par CrossFit Chistera",
    itemListElement: communes.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `https://crossfitchistera.fr/lieux/${c.slug}`,
      name: c.name,
    })),
  },
];

export default function LieuxHub() {
  // Tri par distance croissante
  const sorted = [...communes].sort((a, b) => a.distance_km - b.distance_km);

  return (
    <>
      <SEO
        title="Communes desservies — Box CrossFit Lyon Est — Chistera"
        description="CrossFit Chistera dessert plus de 17 communes autour de Saint-Pierre-de-Chandieu : Mions, Heyrieux, Saint-Bonnet-de-Mure, Toussieu, Chaponnay, Corbas, Vénissieux, Bron…"
        canonical={URL}
        jsonLd={jsonLd}
      />
      <ServicePageLayout
        breadcrumbs={[{ label: "Lieux desservis" }]}
        h1="Communes desservies autour de la box"
        intro="CrossFit Chistera est implanté à Saint-Pierre-de-Chandieu (69780), à la croisée de l'Est lyonnais et du Nord-Isère. La box est facilement accessible depuis plus de 17 communes alentour, par les axes A43, A46, D318, D149 et D518. Tous les adhérents bénéficient de 2 séances d'essai gratuites."
      >
        <h2>Trouvez votre commune</h2>
        <p>Cliquez sur une commune pour découvrir l'itinéraire, le temps de trajet et les cours disponibles depuis chez vous.</p>

        <ul>
          {sorted.map((c) => (
            <li key={c.slug}>
              <Link to={`/lieux/${c.slug}`}>CrossFit {c.name} ({c.cp})</Link> — {c.distance_km} km, {c.drive_min} min via {c.axe}.
            </li>
          ))}
        </ul>

        <h2>Carte interactive</h2>
        <p>La box est située au cœur de Saint-Pierre-de-Chandieu, parking gratuit sur place.</p>
        <div className="aspect-video rounded-md overflow-hidden border border-border my-6">
          <iframe
            title="Localisation CrossFit Chistera"
            src="https://www.google.com/maps?q=CrossFit+Chistera+Saint-Pierre-de-Chandieu&output=embed"
            className="w-full h-full"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>

        <h2>Vous ne voyez pas votre commune ?</h2>
        <p>
          La liste ci-dessus n'est pas exhaustive — la box accueille des adhérents venus de toute la métropole lyonnaise et du Nord-Isère. Si vous habitez à proximité, <Link to="/#contact">contactez-nous</Link> pour réserver vos 2 séances d'essai gratuites.
        </p>
      </ServicePageLayout>
    </>
  );
}
